// utils/migrateActiveField.js
// ONE-TIME MIGRATION SCRIPT
// This script adds 'active: true' field to all users across all workspaces

import { db } from "../firebase";
import { collection, getDocs, doc, updateDoc, writeBatch } from "firebase/firestore";

/**
 * Migrates all users across all workspaces by adding 'active: true' field
 * if it doesn't exist
 */
export async function migrateActiveFieldForAllWorkspaces() {
  console.log("🚀 Starting migration for all workspaces...");
  
  try {
    // Step 1: Get all workspaces
    const workspacesRef = collection(db, "workspaces");
    const workspacesSnapshot = await getDocs(workspacesRef);
    
    if (workspacesSnapshot.empty) {
      console.log("❌ No workspaces found in Firestore");
      return { success: false, message: "No workspaces found" };
    }

    console.log(`✅ Found ${workspacesSnapshot.size} workspace(s)`);

    let totalUsersUpdated = 0;
    let totalUsersChecked = 0;
    const results = [];

    // Step 2: Loop through each workspace
    for (const workspaceDoc of workspacesSnapshot.docs) {
      const workspaceId = workspaceDoc.id;
      console.log(`\n📂 Processing workspace: ${workspaceId}`);

      // Try both 'users' and 'profiles' collections
      const collections = ['users', 'profiles'];
      
      for (const collectionName of collections) {
        try {
          const usersRef = collection(db, "workspaces", workspaceId, collectionName);
          const usersSnapshot = await getDocs(usersRef);

          if (usersSnapshot.empty) {
            console.log(`   ⚠️  ${collectionName} collection is empty, skipping...`);
            continue;
          }

          console.log(`   📋 Found ${usersSnapshot.size} user(s) in ${collectionName}`);

          // Use batch for better performance
          const batch = writeBatch(db);
          let batchCount = 0;
          let updatedInThisBatch = 0;

          // Step 3: Loop through each user
          for (const userDoc of usersSnapshot.docs) {
            totalUsersChecked++;
            const userData = userDoc.data();
            
            // Step 4: Check if 'isActive' field exists
            if (userData.isActive === undefined) {
              // Step 5: Add 'isActive: true' if missing
              const userRef = doc(db, "workspaces", workspaceId, collectionName, userDoc.id);
              batch.update(userRef, { 
                isActive: true,
                updatedAt: new Date()
              });
              
              batchCount++;
              updatedInThisBatch++;
              totalUsersUpdated++;

              console.log(`   ✏️  Updating user: ${userData.name || userData.email || userDoc.id}`);

              // Firestore batch limit is 500, commit when reached
              if (batchCount >= 500) {
                await batch.commit();
                console.log(`   💾 Committed batch of ${batchCount} updates`);
                batchCount = 0;
              }
            } else {
              console.log(`   ✓  User already has 'active' field: ${userData.name || userData.email || userDoc.id}`);
            }
          }

          // Commit remaining updates in batch
          if (batchCount > 0) {
            await batch.commit();
            console.log(`   💾 Committed final batch of ${batchCount} updates`);
          }

          results.push({
            workspace: workspaceId,
            collection: collectionName,
            totalUsers: usersSnapshot.size,
            updated: updatedInThisBatch
          });

        } catch (collectionError) {
          console.error(`   ❌ Error processing ${collectionName} in ${workspaceId}:`, collectionError);
        }
      }
    }

    // Final summary
    console.log("\n" + "=".repeat(60));
    console.log("📊 MIGRATION SUMMARY");
    console.log("=".repeat(60));
    console.log(`Total users checked: ${totalUsersChecked}`);
    console.log(`Total users updated: ${totalUsersUpdated}`);
    console.log(`Workspaces processed: ${workspacesSnapshot.size}`);
    console.log("\n📋 Detailed Results:");
    results.forEach(r => {
      console.log(`  - ${r.workspace} (${r.collection}): ${r.updated}/${r.totalUsers} updated`);
    });
    console.log("=".repeat(60));
    console.log("✅ Migration completed successfully!\n");

    return {
      success: true,
      totalUsersChecked,
      totalUsersUpdated,
      workspacesProcessed: workspacesSnapshot.size,
      details: results
    };

  } catch (error) {
    console.error("❌ Migration failed:", error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Migrates users for a specific workspace only
 * @param {string} workspaceId - The workspace to migrate
 */
export async function migrateActiveFieldForWorkspace(workspaceId) {
  console.log(`🚀 Starting migration for workspace: ${workspaceId}`);
  
  try {
    const collections = ['users', 'profiles'];
    let totalUpdated = 0;
    let totalChecked = 0;

    for (const collectionName of collections) {
      try {
        const usersRef = collection(db, "workspaces", workspaceId, collectionName);
        const usersSnapshot = await getDocs(usersRef);

        if (usersSnapshot.empty) {
          console.log(`⚠️  ${collectionName} collection is empty, skipping...`);
          continue;
        }

        console.log(`📋 Found ${usersSnapshot.size} user(s) in ${collectionName}`);

        const batch = writeBatch(db);
        let batchCount = 0;

        for (const userDoc of usersSnapshot.docs) {
          totalChecked++;
          const userData = userDoc.data();
          
          if (userData.isActive === undefined) {
            const userRef = doc(db, "workspaces", workspaceId, collectionName, userDoc.id);
            batch.update(userRef, { 
              isActive: true,
              updatedAt: new Date()
            });
            
            batchCount++;
            totalUpdated++;
            console.log(`✏️  Updating: ${userData.name || userData.email || userDoc.id}`);
          }
        }

        if (batchCount > 0) {
          await batch.commit();
          console.log(`💾 Committed ${batchCount} updates for ${collectionName}`);
        }

      } catch (error) {
        console.error(`❌ Error processing ${collectionName}:`, error);
      }
    }

    console.log(`\n✅ Migration complete for ${workspaceId}`);
    console.log(`   Users checked: ${totalChecked}`);
    console.log(`   Users updated: ${totalUpdated}\n`);

    return {
      success: true,
      workspaceId,
      totalChecked,
      totalUpdated
    };

  } catch (error) {
    console.error("❌ Migration failed:", error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Dry run - shows what would be updated without actually updating
 */
export async function previewMigration() {
  console.log("👀 Running migration preview (no changes will be made)...\n");
  
  try {
    const workspacesRef = collection(db, "workspaces");
    const workspacesSnapshot = await getDocs(workspacesRef);
    
    console.log(`Found ${workspacesSnapshot.size} workspace(s)\n`);

    const preview = [];
    let totalNeedUpdate = 0;

    for (const workspaceDoc of workspacesSnapshot.docs) {
      const workspaceId = workspaceDoc.id;
      console.log(`📂 ${workspaceId}:`);

      const collections = ['users', 'profiles'];
      
      for (const collectionName of collections) {
        const usersRef = collection(db, "workspaces", workspaceId, collectionName);
        const usersSnapshot = await getDocs(usersRef);

        if (usersSnapshot.empty) continue;

        let needsUpdate = 0;
        let hasField = 0;

        usersSnapshot.docs.forEach(doc => {
          if (doc.data().isActive === undefined) {
            needsUpdate++;
          } else {
            hasField++;
          }
        });

        console.log(`   ${collectionName}: ${needsUpdate} need update, ${hasField} already have field`);
        
        if (needsUpdate > 0) {
          preview.push({
            workspace: workspaceId,
            collection: collectionName,
            needsUpdate
          });
          totalNeedUpdate += needsUpdate;
        }
      }
    }

    console.log("\n" + "=".repeat(60));
    console.log(`📊 PREVIEW SUMMARY: ${totalNeedUpdate} users need 'active' field added`);
    console.log("=".repeat(60) + "\n");

    return { preview, totalNeedUpdate };

  } catch (error) {
    console.error("❌ Preview failed:", error);
    return { error: error.message };
  }
}

